<template>
  <div
    class="relative flex min-h-0 flex-1 flex-col overflow-hidden"
    @dragover.prevent="onDragOver"
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
  >
    <DropZoneOverlay :visible="isDragging" />

    <div class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-6">
      <div>
        <label class="mb-2 block text-sm font-semibold text-gray-700">
          What would you like to know or do?
        </label>
        <textarea
          v-model="prompt"
          rows="6"
          class="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
          placeholder="Example: Analyze the top 5 campaigns by cost and suggest which ones to optimize"
          :disabled="isTokenLimitExceeded"
          @keydown.enter.exact.prevent="handleSubmit"
          @paste="handlePaste"
        />
      </div>

      <PendingAttachmentList
        :attachments="pendingAttachments"
        :warning="attachmentWarning"
        @remove="removePendingAttachment"
      />

      <div
        v-if="isTokenLimitExceeded"
        class="rounded-md bg-orange-50 p-3 text-sm text-orange-800"
      >
        <p class="font-semibold">Monthly AI usage limit reached</p>
        <p>
          Your organization has used its monthly AI allowance. The limit will
          reset in {{ usage.data?.daysUntilRenewal }}
          {{ usage.data?.daysUntilRenewal === 1 ? "day" : "days" }}. Please
          contact your administrator for more information.
        </p>
      </div>

      <div v-if="suggestions.length > 0 && !isTokenLimitExceeded">
        <p class="mb-2 text-xs text-gray-600">Quick suggestions:</p>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="suggestion in suggestions"
            :key="suggestion"
            class="rounded-md border border-gray-300 bg-white px-3 py-1 text-xs text-gray-700 transition hover:border-gray-400 hover:bg-gray-50"
            @click="prompt = suggestion"
          >
            {{ suggestion }}
          </button>
        </div>
      </div>

      <div
        v-if="createSessionMutation.error"
        class="rounded-md bg-red-50 p-3 text-sm text-red-800"
      >
        <p class="font-semibold">Error:</p>
        <p>
          {{ errorMessage || "Failed to start AI agent session" }}
        </p>
      </div>

      <div v-if="recentSessions.length > 0">
        <p class="mb-2 text-xs text-gray-600">Recent chats:</p>
        <div class="space-y-1">
          <button
            v-for="session in recentSessions"
            :key="session.id"
            class="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition hover:bg-gray-100"
            @click="resumeSession(session.id)"
          >
            <Icon name="comment-dots" class="size-4 shrink-0 text-gray-400" />
            <div class="min-w-0 flex-1">
              <p class="truncate text-sm font-medium text-gray-700">
                {{ session.title }}
              </p>
              <p class="text-xs text-gray-400">
                {{ formatRelativeTime(session.modifiedAt) }}
              </p>
            </div>
            <Icon name="angle-right" class="size-3.5 shrink-0 text-gray-300" />
          </button>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-3 border-t border-gray-200 px-6 py-4">
      <!-- Shown unconditionally, unlike the chat panel's copy: no session
        exists yet, so the model this will run against is the organization's
        configured default, which the client has no query for. For an
        organization whose default is not a reasoning model the backend drops
        the value — the cost of not adding a config round trip to the panel
        that has to open instantly. -->
      <ReasoningEffortSelect
        v-model="chatEffort"
        :disabled="isSubmitting || isTokenLimitExceeded"
      />
      <div class="flex items-center justify-end gap-2">
        <input
          ref="fileInput"
          type="file"
          accept="image/png,image/jpeg"
          multiple
          class="hidden"
          @change="handleFileSelect"
        />
        <button
          type="button"
          class="rounded-md p-2 text-gray-400 hover:text-purple-600 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="isSubmitting || isTokenLimitExceeded"
          title="Attach image"
          @click="fileInput?.click()"
        >
          <Icon name="image" class="size-5" />
        </button>
        <button
          class="flex items-center gap-2 rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="
            (!prompt.trim() && pendingAttachments.length === 0) ||
            isSubmitting ||
            isTokenLimitExceeded
          "
          @click="handleSubmit"
        >
          <Icon
            v-if="isSubmitting"
            name="spinner-third-duotone"
            class="size-4 animate-spin"
          />
          <span>Send to Automagic AI</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useRoute } from "vue-router";
import ReasoningEffortSelect from "@/components/AIAgent/ReasoningEffortSelect.vue";
import Icon from "@/components/Core/Icon.vue";
import DropZoneOverlay from "@/components/AIAgent/DropZoneOverlay.vue";
import PendingAttachmentList from "@/components/AIAgent/PendingAttachmentList.vue";
import {
  useCreateAISessionMutation,
  useAISessionsQuery,
  type ChatAttachment,
} from "@/services/ai_agent";
import { getApiErrorMessage } from "@/composables/useAIAgentStream";
import { useAIChatSlideover } from "@/composables/useAIChatSlideover";
import { useIsAITokenLimitExceeded } from "@/services/ai_token_usage";
import {
  usePendingAttachments,
  IMAGE_ONLY_FALLBACK_MESSAGE,
} from "@/composables/usePendingAttachments";
import { DateTime } from "luxon";

const emit = defineEmits({
  sessionCreated: (
    sessionId: number,
    initialPrompt: string,
    _attachments?: ChatAttachment[],
  ) => {
    return typeof sessionId === "number" && typeof initialPrompt === "string";
  },
});

const {
  pendingAttachments,
  attachmentWarning,
  isDragging,
  handleFileSelect,
  handlePaste,
  onDragOver,
  onDragLeave,
  onDrop,
  removePendingAttachment,
  uploadPendingAttachments,
  clearPendingAttachments,
} = usePendingAttachments();

const prompt = ref("");
const fileInput = ref<HTMLInputElement | null>(null);
const route = useRoute();

// Keep in sync with _COMPETITOR_VIEW_NAMES in
// backend/ai_agents/engine/agent_loop.py
const COMPETITOR_VIEW_NAMES = new Set([
  "competitor-monitor",
  "competitor-overview",
  "competitor-posts",
  "competitor-post",
  "competitors",
  "share-of-search",
]);

const DEFAULT_SUGGESTIONS = [
  "What are my top traffic sources?",
  "Show me conversion trends for the last 30 days",
  "Give me a summary across all channels",
  "Show me metrics that need attention",
  "Analyze recent performance trends",
  "What are the key takeaways from my latest data?",
  "Check if any of current local news is relevant to my campaigns. Assert impact severity and likelihood of impact. Include only news that are relevant to my campaigns.",
  "Create a dashboard with an overview of all my ad campaigns",
  "Break down my spending by channel and highlight any budget anomalies",
  "Which keywords are driving conversions but have a below-average quality score?",
  "Compare this month's ROAS to last month across all platforms and suggest where to reallocate budget",
];

const COMPETITOR_SUGGESTIONS = [
  "How are my competitors performing on social media?",
  "Compare my follower count against all competitors",
  "Show me the top competitor posts by engagement",
  "What are my competitors' posting patterns?",
  "Which competitor is growing followers the fastest?",
  "Show me Share of Search trends over the last 3 months",
  "Which keywords do my competitors dominate in search?",
  "Create a competitor monitoring dashboard",
  "Compare my posting frequency to competitors",
  "Show me which competitor posts got the most comments recently",
];

const suggestions = computed(() =>
  COMPETITOR_VIEW_NAMES.has(route.name as string)
    ? COMPETITOR_SUGGESTIONS
    : DEFAULT_SUGGESTIONS,
);

const { pendingPrompt, chatEffort, handleSessionCreated } =
  useAIChatSlideover();
const createSessionMutation = useCreateAISessionMutation();
const { isExceeded: isTokenLimitExceeded, usage } = useIsAITokenLimitExceeded();
const sessions = useAISessionsQuery();

const MAX_RECENT = 5;

const recentSessions = computed(() => {
  if (!sessions.data) return [];
  return sessions.data.slice(0, MAX_RECENT);
});

function formatRelativeTime(timestamp: string): string {
  return DateTime.fromISO(timestamp).toRelative() ?? "";
}

function resumeSession(sessionId: number): void {
  handleSessionCreated(sessionId);
}

const isUploading = ref(false);
const isSubmitting = computed(
  () => isUploading.value || createSessionMutation.isPending,
);

const errorMessage = computed(() =>
  createSessionMutation.error
    ? getApiErrorMessage(createSessionMutation.error)
    : null,
);

watch(
  pendingPrompt,
  (newPrompt) => {
    if (newPrompt) {
      prompt.value = newPrompt;
      handleSubmit();
    }
  },
  { immediate: true },
);

async function handleSubmit() {
  const hasText = prompt.value.trim().length > 0;
  const hasAttachments = pendingAttachments.value.length > 0;
  if (
    (!hasText && !hasAttachments) ||
    isSubmitting.value ||
    isTokenLimitExceeded.value
  ) {
    return;
  }

  // Upload attachments first
  let attachments: ChatAttachment[] | undefined;
  if (hasAttachments) {
    isUploading.value = true;
    const uploaded = await uploadPendingAttachments();
    isUploading.value = false;
    if (!uploaded) return; // upload failed
    attachments = uploaded;
  }

  // Clear pendingPrompt only once we've confirmed we're proceeding
  pendingPrompt.value = null;

  const userPrompt = prompt.value.trim() || IMAGE_ONLY_FALLBACK_MESSAGE;

  try {
    // Create session
    const session = await createSessionMutation.mutateAsync({
      title: userPrompt.substring(0, 50),
    });

    // Reset prompt and attachments
    prompt.value = "";
    clearPendingAttachments();

    // Emit session created — switches to chat view immediately
    emit("sessionCreated", session.id, userPrompt, attachments);
  } catch {
    // Error is displayed via createSessionMutation.error in the template
  }
}
</script>
